import Head from 'next/head';
import { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { FunctionComponent } from 'react';

import usePageViewEvent from '../../../hooks/use-page-view-event';
import { removeYearFromVersion } from '../../../utils/remove-year-from-version';
import useHreflang from '../../../utils/seo/hreflang';
import useOGMeta from '../../../utils/seo/ogmeta';
import useSchema from '../../../utils/seo/schema-org/schema';

const ProductsSchema: FunctionComponent<{
  products: any;
  breadcrumbs: Array<{
    href: LinkProps['href'];
    as: LinkProps['as'];
    text: string;
  }>;
  translatedSlugs: any;
}> = ({ products, breadcrumbs, translatedSlugs }) => {
  const { locale = 'en', asPath, pathname, query, param } = useRouter();
  const localePath = locale !== 'en' ? `/${locale}` : '';
  const { t } = useTranslation();
  const defaultVehicle = t('vehicle-picker:selectVehicleAlt');

  let name = '';
  let description = '';
  let translateIdTitle = '';
  let translateIdDescription = '';
  if (products.search.car) {
    if (
      (products.search.reference && !!products.search.part) ||
      (!products.search.reference && !!products.search.part)
    ) {
      translateIdTitle = 'products:head.part-vehicle-reference.title';
      translateIdDescription =
        'products:head.part-vehicle-reference.description';
    } else {
      if (products.search.reference) {
        translateIdTitle = 'products:head.reference.title';
        translateIdDescription = 'products:head.reference.description';
      } else {
        translateIdTitle = 'products:head.only-vehicle.title';
        translateIdDescription = 'products:head.only-vehicle.description';
      }
    }
  } else {
    if (!products.search.reference && !products.search.part) {
      translateIdTitle = 'products:head.only-vehicle.title';
      translateIdDescription = 'products:head.only-vehicle.description';
    } else {
      if (
        (products.search.reference && !!products.search.part) ||
        (!products.search.reference && !!products.search.part)
      ) {
        translateIdTitle = 'products:head.part-reference.title';
        translateIdDescription = 'products:head.part-reference.description';
      } else {
        if (products.search.reference) {
          translateIdTitle = 'products:head.reference.title';
          translateIdDescription = 'products:head.reference.description';
        }
      }
    }
  }

  name = t(translateIdTitle, {
    part: products.search.part?.name || '',
    reference: products.search.reference || '',
    vehicle: products.search.car
      ? `${products.search.car?.brand_name || ''} ${products.search.car?.model_name || ''
      }${(products.search.car?.version_name &&
        ` ${removeYearFromVersion(products.search.car?.version_name)}`) ||
      ''
      }`
      : defaultVehicle,
    theWord: '',
  });

  description = t(translateIdDescription, {
    part: products.search.part?.name || '',
    reference: products.search.reference || '',
    vehicle: products.search.car
      ? `${products.search.car?.brand_name || ''} ${products.search.car?.model_name || ''
      }${(products.search.car?.version_name &&
        ` ${removeYearFromVersion(products.search.car?.version_name)}`) ||
      ''
      }`
      : defaultVehicle,
    theWord: '',
  });

  const og = useOGMeta({
    title: name,
    description,
    url: process.env.NEXT_PUBLIC_HOST + localePath + asPath,
  });

  const alternateLinks = useHreflang({
    locale,
    route: 'search',
    params: query,
    translatedSlugs: translatedSlugs?.slugs,
  });

  usePageViewEvent(name);

  return (
    <Head>
      <title>{name}</title>
      <meta name="description" content={description} />
      {alternateLinks}
      {og}

      <script {...useSchema({ name, description, breadcrumbs })} />
    </Head>
  );
};

export default ProductsSchema;
